import React, { FC, useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { onRecentError, recordCrash } from 'apollo/recent-errors';
import { takeRememberedCrash } from 'error-reporting/crash-summary';
import { assistantQuestionVar, helpDrawerVar } from 'apollo/reactive-variables/help-drawer';

const VISIBLE_MS = 15 * 1000;
/** At most one offer per two minutes: a burst of failing requests is one problem. */
const QUIET_MS = 2 * 60 * 1000;

/**
 * DD-3780: when a request fails, a short offer next to the help button to ask the help
 * assistant what went wrong. The assistant already gets the recent errors with the question.
 */
const AssistantErrorNudge: FC = () => {
    const { t: translate } = useTranslation(['common', 'help-drawer']);
    const helpVisible = useReactiveVar(helpDrawerVar);
    const [visible, setVisible] = useState(false);
    // The page crashed (red screen) just before this load: a different offer and question.
    const [afterCrash, setAfterCrash] = useState(false);
    const lastShownAt = useRef(0);

    useEffect(() => {
        const crash = takeRememberedCrash();
        if (crash) {
            recordCrash(crash);
            lastShownAt.current = Date.now();
            setAfterCrash(true);
            setVisible(true);
        }
        return onRecentError(() => {
            const now = Date.now();
            if (helpDrawerVar() || now - lastShownAt.current < QUIET_MS) {
                return;
            }
            lastShownAt.current = now;
            setAfterCrash(false);
            setVisible(true);
        });
    }, []);

    useEffect(() => {
        if (!visible) {
            return;
        }
        const timer = setTimeout(() => setVisible(false), VISIBLE_MS);
        return () => clearTimeout(timer);
    }, [visible]);

    if (!visible || helpVisible) {
        return null;
    }

    const askAssistant = () => {
        setVisible(false);
        assistantQuestionVar(
            translate(afterCrash ? 'help-drawer:ASSISTANT_WHY_DID_IT_CRASH' : 'help-drawer:ASSISTANT_WHY_DID_IT_FAIL'),
        );
        helpDrawerVar(true);
    };

    return (
        <Paper
            elevation={6}
            role="status"
            sx={{
                position: 'fixed',
                right: 20,
                bottom: 72,
                zIndex: 101,
                px: 2,
                py: 1.5,
                maxWidth: 320,
                borderRadius: 2,
            }}
            data-test="assistant-error-nudge"
        >
            <Box display="flex" alignItems="flex-start" gap={1}>
                <Typography variant="body2" flexGrow={1}>
                    {translate(afterCrash ? 'help-drawer:ASSISTANT_CRASH_NUDGE' : 'help-drawer:ASSISTANT_ERROR_NUDGE')}
                </Typography>
                <IconButton size="small" aria-label={translate('common:CLOSE')} onClick={() => setVisible(false)}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            <Button variant="contained" size="small" sx={{ mt: 1 }} onClick={askAssistant}>
                {translate('help-drawer:ASSISTANT_ASK_WHAT_WENT_WRONG')}
            </Button>
        </Paper>
    );
};

export default AssistantErrorNudge;
